import React from 'react';
import { Save, ToggleLeft, ToggleRight, Wallet } from 'lucide-react';
import { formatCurrency } from '../../services/api';

export function TaxSummary({
  form, isSales, autoGstEnabled, onToggleGst, onChange,
  subtotal, isIntra, gstRate, cgst, sgst, igst, gstTotal, grossAmount,
  tdsPercentage, tdsAmount, isTdsPayable,
  advanceEngineEnabled, advanceBalance, partyAdvances, advAdjusted,
  autoRound, setAutoRound, roundOff, grandTotal, netPayable,
  saving, onSave,
}) {
  const gstLabel = isSales ? 'Output' : 'Input';
  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 sticky top-6 space-y-3">
      <h3 className="font-heading font-semibold text-slate-800 dark:text-slate-200">Tax Summary</h3>
      {/* Per-invoice Auto-GST toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">Auto GST</span>
        <button type="button" onClick={onToggleGst} className="flex items-center gap-1 text-xs font-semibold" data-testid="auto-gst-toggle" aria-pressed={autoGstEnabled}>
          {autoGstEnabled ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
          <span className={autoGstEnabled ? 'text-emerald-700' : 'text-slate-500'}>{autoGstEnabled ? 'ON' : 'OFF'}</span>
        </button>
      </div>
      {autoGstEnabled ? (
        <div><label className="form-label">GST Rate (%)</label>
          <input type="number" value={form.gst_rate} onChange={(e) => onChange('gst_rate', e.target.value)} className="form-input font-mono text-right" min="0" max="100" data-testid="gst-rate-input" />
        </div>
      ) : (
        <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5" data-testid="gst-disabled-notice">No GST on this invoice. Toggle ON above to apply GST.</p>
      )}
      <div className="border-t border-slate-100 pt-3 space-y-2">
        <div className="flex justify-between text-sm"><span className="text-slate-500">Taxable Value</span><span className="font-mono" data-testid="subtotal">Rs. {formatCurrency(subtotal)}</span></div>
        {autoGstEnabled && (isIntra ? (<><div className="flex justify-between text-sm"><span className="text-slate-500">{gstLabel} CGST ({gstRate/2}%)</span><span className="font-mono">Rs. {formatCurrency(cgst)}</span></div>
        <div className="flex justify-between text-sm"><span className="text-slate-500">{gstLabel} SGST ({gstRate/2}%)</span><span className="font-mono">Rs. {formatCurrency(sgst)}</span></div></>) :
        (form.place_of_supply || form.shipped_to_state) ? <div className="flex justify-between text-sm"><span className="text-slate-500">{gstLabel} IGST ({gstRate}%)</span><span className="font-mono">Rs. {formatCurrency(igst)}</span></div> : null)}
        <div className="flex justify-between text-sm border-t border-slate-100 pt-2"><span className="text-slate-600 font-medium">Gross Amount</span><span className="font-mono font-medium">Rs. {formatCurrency(grossAmount)}</span></div>
      </div>

      {/* TDS Section */}
      <div className="border-t border-slate-100 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">TDS Deduction</span>
          <button onClick={() => onChange('tds_enabled', !form.tds_enabled)} className="flex items-center gap-1 text-xs" data-testid="tds-toggle">
            {form.tds_enabled ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-slate-400" />}
            <span className={form.tds_enabled ? 'text-emerald-600 font-medium' : 'text-slate-400'}>{form.tds_enabled ? 'ON' : 'OFF'}</span>
          </button>
        </div>
        {form.tds_enabled && (
          <div className="space-y-2 bg-slate-50 rounded-lg p-3">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="form-label text-xs">TDS %</label><input type="text" inputMode="decimal" value={form.tds_percentage} onChange={e => { const v = e.target.value; if (v === '' || v === '-' || /^-?\d*\.?\d{0,2}$/.test(v)) onChange('tds_percentage', v); }} className="form-input font-mono text-right text-sm" data-testid="tds-percentage" placeholder="e.g. 1.60" /></div>
              <div><label className="form-label text-xs">Section</label><select value={form.tds_section} onChange={e => onChange('tds_section', e.target.value)} className="form-input text-sm" data-testid="tds-section">
                <option value="">Select</option>
                <option value="194C">194C - Contractor</option>
                <option value="194J">194J - Professional</option>
                <option value="194H">194H - Commission</option>
                <option value="194I">194I - Rent</option>
                <option value="194A">194A - Interest</option>
                <option value="194Q">194Q - Purchase of Goods</option>
              </select></div>
            </div>
            <div className={`flex justify-between text-sm ${isTdsPayable ? 'text-orange-700' : 'text-red-700'}`}>
              <span>TDS {isTdsPayable ? 'Payable' : (isSales ? 'Receivable' : 'Deduction')} ({Math.abs(tdsPercentage)}%)</span>
              <span className="font-mono font-medium" data-testid="tds-amount">(-Rs. {formatCurrency(tdsAmount)})</span>
            </div>
          </div>
        )}
      </div>

      {/* Advance Adjustment */}
      {advanceEngineEnabled && (
      <div className="border-t border-slate-100 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5"><Wallet className="w-4 h-4" /> Advance Adjustment</span>
          {advanceBalance > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">Bal: Rs.{formatCurrency(advanceBalance)}</span>}
        </div>
        {advanceBalance > 0 ? (
          <div className="space-y-2 bg-blue-50/50 rounded-lg p-3">
            <div className="text-xs text-slate-600 space-y-1">
              {partyAdvances.filter(a => a.balance_amount > 0).map((a, i) => (
                <div key={i} className="flex justify-between"><span>{a.voucher_number} ({a.date})</span><span className="font-mono">Rs.{formatCurrency(a.balance_amount)}</span></div>
              ))}
            </div>
            <div>
              <label className="form-label text-xs">Amount to Adjust</label>
              <input type="number" step="0.01" min="0" max={Math.min(grandTotal, advanceBalance)}
                value={form.advance_adjusted || ''} onChange={e => onChange('advance_adjusted', parseFloat(e.target.value) || 0)}
                className="form-input font-mono text-right text-sm" data-testid="advance-adjust-input"
                placeholder={`Max: ${formatCurrency(Math.min(grandTotal, advanceBalance))}`} />
            </div>
            {advAdjusted > 0 && <div className="flex justify-between text-sm text-blue-700"><span>Advance Adjusted</span><span className="font-mono font-medium" data-testid="advance-adjusted-display">(Rs. {formatCurrency(advAdjusted)})</span></div>}
          </div>
        ) : (
          <p className="text-xs text-slate-400">{form.billed_to_name ? 'No unadjusted advances for this party' : 'Select a party to check advances'}</p>
        )}
      </div>
      )}

      {/* Round Off + Grand Total */}
      <div className="border-t border-slate-100 pt-2 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Round Off</span>
            <label className="flex items-center gap-1 text-xs"><input type="checkbox" checked={autoRound} onChange={e => setAutoRound(e.target.checked)} className="w-3 h-3" /><span className="text-slate-400">Auto</span></label>
          </div>
          {autoRound ? <span className="font-mono">Rs. {roundOff.toFixed(2)}</span> : <input type="number" step="0.01" value={form.round_off_manual} onChange={e => onChange('round_off_manual', e.target.value)} className="form-input w-24 font-mono text-right text-sm py-0.5 px-2" data-testid="round-off-input" />}
        </div>
        <div className="border-t-2 border-slate-900 pt-2 space-y-1">
          <div className="flex justify-between"><span className="font-heading font-semibold text-sm">Invoice Total</span><span className="font-mono font-semibold" data-testid="grand-total">Rs. {formatCurrency(grandTotal)}</span></div>
          {advAdjusted > 0 && <div className="flex justify-between text-blue-700 text-sm"><span>Less: Advance</span><span className="font-mono">(Rs. {formatCurrency(advAdjusted)})</span></div>}
          <div className="flex justify-between"><span className="font-heading font-bold text-lg">Net Payable</span><span className="font-mono font-bold text-lg" data-testid="net-payable">Rs. {formatCurrency(netPayable)}</span></div>
        </div>
        {(form.place_of_supply || form.shipped_to_state) && <p className="text-[10px] text-slate-400 bg-slate-50 rounded px-2 py-1">{isIntra ? `Intra-state (${gstLabel} CGST + SGST)` : `Inter-state (${gstLabel} IGST)`}</p>}
      </div>
      <button onClick={onSave} disabled={saving} className="btn-accent w-full flex items-center justify-center gap-2 mt-4" data-testid="save-invoice-btn">
        <Save className="w-4 h-4" /> {saving ? 'Saving...' : (isSales ? 'Save Sales Invoice' : 'Save Purchase Voucher')}
      </button>
    </div>
  );
}
